import { useCallback, useState } from 'react';
import { downloadExport, downloadTableExport, saveBlob } from '../services/exportService';
import { useHierarchyFilter } from './useHierarchyFilter';
import { rowsToCsv, rowsToTable } from '../components/shared/exportLogic';

export { rowsToCsv, rowsToTable };

const slug = (name) => String(name).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

/**
 * Export hook behind the page-header Export menu and DataTable.
 * - endpoint: server export (path under /api); receives the global filter params plus `format` (csv | xlsx | pdf).
 * - columns + rows: what the table shows, used when there is no endpoint: CSV in the browser, Excel through POST export/table.
 * onDone(result) receives { success, filename, error } so callers can toast.
 */
export function useExport({ name, endpoint, params, columns, rows, onDone }) {
  const { params: filterParams } = useHierarchyFilter();
  const [busy, setBusy] = useState(false);

  const exportAs = useCallback(
    async (format) => {
      setBusy(true);
      const today = new Date().toISOString().slice(0, 10);
      const fallback = `${slug(name)}-${today}.${format}`;
      let result;
      try {
        if (endpoint) {
          result = await downloadExport(endpoint, { ...filterParams, ...params, format }, fallback);
        } else if (format === 'csv' && columns && rows) {
          const blob = new Blob([`﻿${rowsToCsv(columns, rows)}`], { type: 'text/csv;charset=utf-8' });
          saveBlob(blob, fallback);
          result = { success: true, filename: fallback };
        } else if (format === 'xlsx' && columns && rows) {
          result = await downloadTableExport({ ...rowsToTable(name, columns, rows), format }, fallback);
        } else {
          result = { success: false, error: `${format.toUpperCase()} export isn't available for ${name} yet` };
        }
      } finally {
        setBusy(false);
      }
      if (onDone) onDone(result);
      return result;
    },
    [name, endpoint, params, columns, rows, filterParams, onDone]
  );

  return { exportAs, busy };
}

export default useExport;
